/**
 * A request with no timeout of its own never settles on a connection that
 * stops moving: the "Uploading…" state spun forever (App Store reviews,
 * 2026-09). The watchdog fires once NO bytes have moved for `stallMs`; every
 * progress event re-arms it, so a slow upload that keeps moving is never cut.
 */
export const UPLOAD_STALL_MS = 60_000;

export interface UploadTimers {
  setTimer: (run: () => void, ms: number) => unknown;
  clearTimer: (handle: unknown) => void;
}

export const DEFAULT_UPLOAD_TIMERS: UploadTimers = {
  setTimer: (run, ms) => setTimeout(run, ms),
  clearTimer: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

export interface StallWatchdog {
  /** Bytes moved: start the window again. */
  kick(): void;
  stop(): void;
}

export function createStallWatchdog({
  stallMs = UPLOAD_STALL_MS,
  onStall,
  timers = DEFAULT_UPLOAD_TIMERS,
}: {
  stallMs?: number;
  onStall: () => void;
  timers?: UploadTimers;
}): StallWatchdog {
  let handle: unknown;
  let stopped = false;
  const arm = () => {
    timers.clearTimer(handle);
    if (!stopped) handle = timers.setTimer(onStall, stallMs);
  };
  arm();
  return {
    kick: arm,
    stop: () => {
      stopped = true;
      timers.clearTimer(handle);
    },
  };
}
